// ============================================
// SERVICE API TRAKT.TV (appel direct depuis le frontend)
// Le CLIENT_ID est exposé mais c'est une clé publique (comme une API key)
// ============================================

const TRAKT_BASE_URL = 'https://api.trakt.tv';
const TRAKT_USERNAME = import.meta.env.VITE_TRAKT_USERNAME || 'pierre337';

// Client ID exposé (clé publique, pas secrète)
const TRAKT_CLIENT_ID = '47a3df5727a512a840759b8f32de584f0ef5948437b6d866a35588058f1d4c00';

// Log pour debug
console.log('[Trakt] Username from env:', import.meta.env.VITE_TRAKT_USERNAME);
console.log('[Trakt] Using username:', TRAKT_USERNAME);

// Types
export interface TraktUser {
  username: string;
  private: boolean;
  name: string;
  vip: boolean;
  ids: {
    slug: string;
  };
  joined_at: string;
  location?: string;
  about?: string;
  gender?: string;
  age?: number;
  images?: {
    avatar?: {
      full?: string;
    };
  };
}

export interface TraktMovie {
  title: string;
  year: number;
  ids: {
    trakt: number;
    slug: string;
    imdb: string;
    tmdb: number;
  };
  poster?: string;
}

export interface TraktShow {
  title: string;
  year: number;
  ids: {
    trakt: number;
    slug: string;
    tvdb: number;
    imdb: string;
    tmdb: number;
  };
  poster?: string;
}

export interface TraktEpisode {
  season: number;
  number: number;
  title: string;
  ids: {
    trakt: number;
    tvdb: number;
    imdb: string;
    tmdb: number;
  };
}

export interface RatedMovie {
  rating: number;
  type: string;
  movie: TraktMovie;
  rated_at: string;
}

export interface WatchedMovie {
  plays: number;
  watched_at: string;
  last_watched_at?: string;
  last_updated_at?: string;
  movie: TraktMovie;
  rating?: number;
}

export interface WatchedEpisode {
  plays: number;
  watched_at: string;
  last_watched_at?: string;
  episode: TraktEpisode;
  show: TraktShow;
  rating?: number;
}

export interface CurrentlyWatching {
  expires_at: string;
  started_at: string;
  action: string;
  type: string;
  movie?: TraktMovie;
  show?: TraktShow;
  episode?: TraktEpisode;
  progress?: number;
}

export interface UserStats {
  movies: {
    plays: number;
    watched: number;
    minutes: number;
    collected: number;
    ratings: number;
    comments: number;
    genres?: { name: string; count: number; }[];
  };
  shows: {
    watched: number;
    collected: number;
    ratings: number;
    comments: number;
  };
  episodes: {
    plays: number;
    watched: number;
    minutes: number;
    collected: number;
    ratings: number;
    comments: number;
  };
  network?: {
    friends: number;
    followers: number;
    following: number;
  };
}

export interface WatchedGenre {
  name: string;
  count: number;
  percentage?: number;
}

export interface Comment {
  id: number;
  parent_id: number;
  created_at: string;
  updated_at: string;
  comment: string;
  spoiler: boolean;
  review: boolean;
  replies: number;
  likes: number;
  user_rating?: number;
  movie?: TraktMovie;
  show?: TraktShow;
  episode?: TraktEpisode;
}

// Fonction helper pour appeler l'API Trakt directement
const fetchFromTrakt = async (path: string) => {
  const url = `${TRAKT_BASE_URL}${path}`;
  console.log(`[Trakt] Calling ${url}`);
  
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'trakt-api-version': '2',
        'trakt-api-key': TRAKT_CLIENT_ID,
      },
    });
    
    console.log(`[Trakt] Response status: ${response.status}`);
    
    // 204 No Content = réponse valide (par exemple quand on ne regarde rien)
    if (response.status === 204) {
      console.log(`[Trakt] 204 No Content for ${path}`);
      return null;
    }
    
    const text = await response.text();
    console.log(`[Trakt] Response body length:`, text.length);
    console.log(`[Trakt] Response body preview:`, text.substring(0, 200));
    
    if (!response.ok) {
      console.error(`[Trakt] Error ${response.status}:`, text.substring(0, 500));
      throw new Error(`Trakt API error: ${response.status}`);
    }
    
    if (!text || text.trim() === '') {
      console.error('[Trakt] Empty response body');
      throw new Error('Empty response from Trakt API');
    }
    
    try {
      const data = JSON.parse(text);
      console.log(`[Trakt] Success for ${path}`);
      return data;
    } catch (parseErr) {
      console.error('[Trakt] JSON parse error:', parseErr);
      console.error('[Trakt] Raw response:', text.substring(0, 500));
      throw new Error('Invalid JSON response from Trakt API');
    }
  } catch (err) {
    console.error(`[Trakt] Error for ${path}:`, err);
    throw err;
  }
};

// Fonction helper pour appeler TMDB via le proxy serveur
// La clé API est cachée côté serveur
const fetchFromTMDB = async (path: string) => {
  const url = `/api/tmdb?path=${encodeURIComponent(path)}`;
  console.log(`[TMDB Proxy] Calling ${path}`);
  
  const response = await fetch(url);
  
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error(`[TMDB Proxy] Error ${response.status}:`, errorText);
    throw new Error(`TMDB API error: ${response.status} - ${errorText}`);
  }
  
  return await response.json();
};

// Vérifier si configuré (on vérifie juste si on a des données)
export const isTraktConfigured = (): boolean => {
  return true; // Toujours vrai car le serveur gère les clés
};

// Récupérer le profil utilisateur
export const fetchUserProfile = async (): Promise<TraktUser | null> => {
  const data = await fetchFromTrakt(`/users/${TRAKT_USERNAME}`);
  return data;
};

// Récupérer les statistiques utilisateur
export const fetchUserStats = async (): Promise<UserStats | null> => {
  const data = await fetchFromTrakt(`/users/${TRAKT_USERNAME}/stats`);
  return data;
};

// Helper pour attendre
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Enrichir les films avec les posters TMDB (avec rate limiting)
const enrichMoviesWithPosters = async (movies: RatedMovie[]): Promise<RatedMovie[]> => {
  const enriched: RatedMovie[] = [];
  
  for (const item of movies) {
    if (item.movie?.ids?.tmdb) {
      try {
        const poster = await fetchMoviePoster(item.movie.ids.tmdb);
        if (poster) {
          enriched.push({ ...item, movie: { ...item.movie, poster } });
          continue;
        }
      } catch (error) {
        console.warn(`Failed to fetch poster for ${item.movie.title}:`, error);
      }
    }
    enriched.push(item);
    // Petit délai pour éviter le rate limiting
    await delay(50);
  }
  
  return enriched;
};

// Récupérer les films les mieux notés (limité à 10 pour éviter de surcharger l'API)
export const fetchTopRatedMovies = async (limit: number = 10): Promise<RatedMovie[]> => {
  const data = await fetchFromTrakt(`/users/${TRAKT_USERNAME}/ratings/movies`);
  // Trier par rating décroissant et limiter à 10
  const sorted = data.sort((a: RatedMovie, b: RatedMovie) => b.rating - a.rating).slice(0, limit);
  // Enrichir avec les posters
  return await enrichMoviesWithPosters(sorted);
};

// Récupérer ce que l'utilisateur est en train de regarder
export const fetchCurrentlyWatching = async (): Promise<CurrentlyWatching | null> => {
  const data = await fetchFromTrakt(`/users/${TRAKT_USERNAME}/watching`);

  // Si on regarde quelque chose, on enrichit avec le poster TMDB
  if (data) {
    if (data.type === 'movie' && data.movie?.ids?.tmdb) {
      try {
        const poster = await fetchMoviePoster(data.movie.ids.tmdb);
        if (poster) {
          data.movie.poster = poster;
        }
      } catch (error) {
        console.warn(`Failed to fetch poster for ${data.movie.title}:`, error);
      }
    } else if (data.type === 'episode' && data.show?.ids?.tmdb) {
      try {
        const poster = await fetchTVPoster(data.show.ids.tmdb);
        if (poster) {
          data.show.poster = poster;
        }
      } catch (error) {
        console.warn(`Failed to fetch poster for ${data.show.title}:`, error);
      }
    }
  }

  return data;
};

// Récupérer l'historique récent avec ratings
export const fetchRecentHistory = async (limit: number = 8): Promise<{ movies: WatchedMovie[], episodes: WatchedEpisode[] }> => {
  // Récupérer l'historique et les ratings en parallèle
  const [historyData, movieRatings, episodeRatings] = await Promise.all([
    fetchFromTrakt(`/users/${TRAKT_USERNAME}/history`),
    fetchFromTrakt(`/users/${TRAKT_USERNAME}/ratings/movies`).catch(() => []),
    fetchFromTrakt(`/users/${TRAKT_USERNAME}/ratings/episodes`).catch(() => []),
  ]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const movies = historyData.filter((item: any) => item.type === 'movie').slice(0, limit);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const episodes = historyData.filter((item: any) => item.type === 'episode').slice(0, limit);

  // Créer des maps pour les ratings rapides
  const movieRatingsMap = new Map(movieRatings.map((r: RatedMovie) => [r.movie.ids.trakt, r.rating]));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const episodeRatingsMap = new Map(episodeRatings.map((r: any) => [r.episode.ids.trakt, r.rating]));

  // Enrichir avec les posters et les ratings (séquentiellement pour éviter le rate limiting)
  const moviesWithPosters: WatchedMovie[] = [];
  for (const item of movies) {
    let updated: WatchedMovie = { ...item };
    // Ajouter le rating si existe
    const rating = movieRatingsMap.get(item.movie.ids.trakt);
    if (rating && typeof rating === 'number') updated = { ...updated, rating };
    // Ajouter le poster
    if (item.movie?.ids?.tmdb) {
      try {
        const poster = await fetchMoviePoster(item.movie.ids.tmdb);
        if (poster) {
          updated = { ...updated, movie: { ...item.movie, poster } };
        }
      } catch (error) {
        console.warn(`Failed to fetch poster for ${item.movie.title}:`, error);
      }
      await delay(50);
    }
    moviesWithPosters.push(updated);
  }

  const episodesWithPosters: WatchedEpisode[] = [];
  for (const item of episodes) {
    let updated: WatchedEpisode = { ...item };
    // Ajouter le rating si existe
    const rating = episodeRatingsMap.get(item.episode.ids.trakt);
    if (rating && typeof rating === 'number') updated = { ...updated, rating };
    // Ajouter le poster
    if (item.show?.ids?.tmdb) {
      try {
        const poster = await fetchTVPoster(item.show.ids.tmdb);
        if (poster) {
          updated = { ...updated, show: { ...item.show, poster } };
        }
      } catch (error) {
        console.warn(`Failed to fetch poster for ${item.show.title}:`, error);
      }
      await delay(50);
    }
    episodesWithPosters.push(updated);
  }

  return { movies: moviesWithPosters, episodes: episodesWithPosters };
};

// Récupérer les posters TMDB via le proxy
export const fetchMoviePoster = async (tmdbId: number): Promise<string | null> => {
  try {
    console.log(`[TMDB] Fetching poster for movie ${tmdbId}`);
    const data = await fetchFromTMDB(`/movie/${tmdbId}`);
    if (data.poster_path) {
      const url = `https://image.tmdb.org/t/p/w342${data.poster_path}`;
      console.log(`[TMDB] Got poster: ${url}`);
      return url;
    }
    console.warn(`[TMDB] No poster for movie ${tmdbId}`);
    return null;
  } catch (error) {
    console.error(`[TMDB] Error fetching poster for ${tmdbId}:`, error);
    return null;
  }
};

export const fetchTVPoster = async (tmdbId: number): Promise<string | null> => {
  try {
    console.log(`[TMDB] Fetching poster for TV ${tmdbId}`);
    const data = await fetchFromTMDB(`/tv/${tmdbId}`);
    if (data.poster_path) {
      const url = `https://image.tmdb.org/t/p/w342${data.poster_path}`;
      console.log(`[TMDB] Got poster: ${url}`);
      return url;
    }
    console.warn(`[TMDB] No poster for TV ${tmdbId}`);
    return null;
  } catch (error) {
    console.error(`[TMDB] Error fetching poster for ${tmdbId}:`, error);
    return null;
  }
};

// Données de démonstration
export const getMockData = () => ({
  user: {
    username: 'pierre337',
    name: 'Pierre T',
    private: false,
    vip: true,
    ids: { slug: 'pierre337' },
    joined_at: '2020-01-01T00:00:00.000Z',
    location: 'Bordeaux, France',
    about: 'Passionné de cinéma et de séries',
  } as TraktUser,
  
  stats: {
    movies: { plays: 450, watched: 320, minutes: 38400, collected: 150, ratings: 280, comments: 5 },
    shows: { watched: 45, collected: 30, ratings: 120, comments: 2 },
    episodes: { plays: 2800, watched: 2100, minutes: 92400, collected: 800, ratings: 450, comments: 8 },
    network: { friends: 25, followers: 12, following: 18 },
  } as UserStats,
  
  topMovies: [
    { rating: 10, type: 'movie', movie: { title: 'The Dark Knight', year: 2008, ids: { trakt: 2, slug: 'the-dark-knight-2008', imdb: 'tt0468569', tmdb: 155 }, poster: 'https://image.tmdb.org/t/p/w342/qJ2tW6WMUDux911r6m7haRef0WH.jpg' }, rated_at: '2023-02-20T15:30:00.000Z' },
    { rating: 10, type: 'movie', movie: { title: 'Inception', year: 2010, ids: { trakt: 1, slug: 'inception-2010', imdb: 'tt1375666', tmdb: 27205 }, poster: 'https://image.tmdb.org/t/p/w342/9gk7adHYeDvHkCSEqAvQNLV5Uge.jpg' }, rated_at: '2023-01-15T10:00:00.000Z' },
    { rating: 10, type: 'movie', movie: { title: 'Interstellar', year: 2014, ids: { trakt: 3, slug: 'interstellar-2014', imdb: 'tt0816692', tmdb: 157336 }, poster: 'https://image.tmdb.org/t/p/w342/gEU2QniL6C8z19uVOtYnZ5UYj52.jpg' }, rated_at: '2023-03-10T20:00:00.000Z' },
    { rating: 10, type: 'movie', movie: { title: 'The Godfather', year: 1972, ids: { trakt: 7, slug: 'the-godfather-1972', imdb: 'tt0068646', tmdb: 238 }, poster: 'https://image.tmdb.org/t/p/w342/3bhkrj58Vtu7enYsRolD1fZdja1.jpg' }, rated_at: '2023-07-10T18:00:00.000Z' },
    { rating: 10, type: 'movie', movie: { title: 'Pulp Fiction', year: 1994, ids: { trakt: 4, slug: 'pulp-fiction-1994', imdb: 'tt0110912', tmdb: 680 }, poster: 'https://image.tmdb.org/t/p/w342/d5iIlFn5s0ImszYzBPb8JPIfbXD.jpg' }, rated_at: '2023-04-05T18:45:00.000Z' },
    { rating: 9, type: 'movie', movie: { title: 'The Matrix', year: 1999, ids: { trakt: 5, slug: 'the-matrix-1999', imdb: 'tt0133093', tmdb: 603 }, poster: 'https://image.tmdb.org/t/p/w342/f89U3ADr1oiB1s9GkdPOEpXUk5H.jpg' }, rated_at: '2023-05-12T22:15:00.000Z' },
    { rating: 9, type: 'movie', movie: { title: 'Goodfellas', year: 1990, ids: { trakt: 8, slug: 'goodfellas-1990', imdb: 'tt0099685', tmdb: 769 }, poster: 'https://image.tmdb.org/t/p/w342/aKuFiU82s5ISJpGZp7YkIr3kCUd.jpg' }, rated_at: '2023-08-15T20:30:00.000Z' },
    { rating: 9, type: 'movie', movie: { title: 'The Shawshank Redemption', year: 1994, ids: { trakt: 9, slug: 'the-shawshank-redemption-1994', imdb: 'tt0111161', tmdb: 278 }, poster: 'https://image.tmdb.org/t/p/w342/q6y0Go1tsGEsmtFryDOJo3dEmqu.jpg' }, rated_at: '2023-09-01T14:00:00.000Z' },
    { rating: 9, type: 'movie', movie: { title: 'Forrest Gump', year: 1994, ids: { trakt: 12, slug: 'forrest-gump-1994', imdb: 'tt0109830', tmdb: 13 }, poster: 'https://image.tmdb.org/t/p/w342/arw2vcBveWOVZr6pxd9XTd1TdQa.jpg' }, rated_at: '2023-10-05T19:30:00.000Z' },
    { rating: 9, type: 'movie', movie: { title: 'The Silence of the Lambs', year: 1991, ids: { trakt: 13, slug: 'the-silence-of-the-lambs-1991', imdb: 'tt0102926', tmdb: 274 }, poster: 'https://image.tmdb.org/t/p/w342/rplLJ2hPcOQmkFhTqUte0MkEaO2.jpg' }, rated_at: '2023-11-12T21:00:00.000Z' },
    { rating: 8, type: 'movie', movie: { title: 'Fight Club', year: 1999, ids: { trakt: 6, slug: 'fight-club-1999', imdb: 'tt0137523', tmdb: 550 }, poster: 'https://image.tmdb.org/t/p/w342/pB8BM7pdSp6B6Ih7QZ4DrQ3PmJK.jpg' }, rated_at: '2023-06-01T19:00:00.000Z' },
    { rating: 8, type: 'movie', movie: { title: 'The Lord of the Rings: The Return of the King', year: 2003, ids: { trakt: 14, slug: 'the-lord-of-the-rings-the-return-of-the-king-2003', imdb: 'tt0167260', tmdb: 122 }, poster: 'https://image.tmdb.org/t/p/w342/rCzpDGLbOoPwLjy3OAm5NUPOTrC.jpg' }, rated_at: '2023-12-20T16:00:00.000Z' },
    { rating: 8, type: 'movie', movie: { title: 'The Avengers', year: 2012, ids: { trakt: 15, slug: 'the-avengers-2012', imdb: 'tt0848228', tmdb: 24428 }, poster: 'https://image.tmdb.org/t/p/w342/RYMX2wcKCBAr24UyPD7xwmjaTn.jpg' }, rated_at: '2024-01-10T20:00:00.000Z' },
    { rating: 8, type: 'movie', movie: { title: 'Django Unchained', year: 2012, ids: { trakt: 16, slug: 'django-unchained-2012', imdb: 'tt1853728', tmdb: 68718 }, poster: 'https://image.tmdb.org/t/p/w342/7oWY8VDWW7thTzWh3OKYRkWUlD5.jpg' }, rated_at: '2024-02-14T19:30:00.000Z' },
    { rating: 8, type: 'movie', movie: { title: 'Inglourious Basterds', year: 2009, ids: { trakt: 17, slug: 'inglourious-basterds-2009', imdb: 'tt0361748', tmdb: 16869 }, poster: 'https://image.tmdb.org/t/p/w342/6JxWwkTz6Ddx4dVjIBms39S8q91.jpg' }, rated_at: '2024-03-01T18:45:00.000Z' },
  ] as RatedMovie[],
  
  currentlyWatching: null as CurrentlyWatching | null,
  
  recentHistory: {
    movies: [
      { plays: 1, rating: 8, watched_at: '2024-01-20T20:00:00.000Z', movie: { title: 'The Housemaid', year: 2025, ids: { trakt: 10, slug: 'the-housemaid-2025', imdb: 'tt1234567', tmdb: 1234567 }, poster: 'https://image.tmdb.org/t/p/w342/8Gxv8gSFCU0XGDykEGv7zR1n2ua.jpg' } },
      { plays: 1, rating: 9, watched_at: '2024-01-18T21:30:00.000Z', movie: { title: 'Dune: Part Two', year: 2024, ids: { trakt: 11, slug: 'dune-part-two-2024', imdb: 'tt15239678', tmdb: 693134 }, poster: 'https://image.tmdb.org/t/p/w342/1pdfLvkbY9ohJlCjQH2CZjjYVvJ.jpg' } },
      { plays: 1, rating: 7, watched_at: '2024-01-15T19:00:00.000Z', movie: { title: 'Poor Things', year: 2023, ids: { trakt: 12, slug: 'poor-things-2023', imdb: 'tt14208870', tmdb: 792307 }, poster: 'https://image.tmdb.org/t/p/w342/kCGlIMHnOm8JPXq3rXM6c5wMxcT.jpg' } },
      { plays: 1, rating: 8, watched_at: '2024-01-10T20:30:00.000Z', movie: { title: 'Killers of the Flower Moon', year: 2023, ids: { trakt: 13, slug: 'killers-of-the-flower-moon-2023', imdb: 'tt5537002', tmdb: 466420 }, poster: 'https://image.tmdb.org/t/p/w342/dB6cKoOKq0Wybh8JRkcpw6tZew.jpg' } },
    ] as WatchedMovie[],
    episodes: [
      { plays: 1, rating: 9, watched_at: '2024-01-19T22:00:00.000Z', episode: { season: 1, number: 8, title: 'The Episode', ids: { trakt: 100, tvdb: 1, imdb: 'tt100', tmdb: 100 } }, show: { title: 'True Detective', year: 2014, ids: { trakt: 1, slug: 'true-detective', tvdb: 270633, imdb: 'tt2356777', tmdb: 46648 }, poster: 'https://image.tmdb.org/t/p/w342/cuV2O5S5VwD1MpR9j8p7d7qKnds.jpg' } },
      { plays: 1, rating: 8, watched_at: '2024-01-17T21:00:00.000Z', episode: { season: 4, number: 2, title: 'Another Episode', ids: { trakt: 101, tvdb: 2, imdb: 'tt101', tmdb: 101 } }, show: { title: 'The Crown', year: 2016, ids: { trakt: 2, slug: 'the-crown', tvdb: 305288, imdb: 'tt4786824', tmdb: 65494 }, poster: 'https://image.tmdb.org/t/p/w342/e3aqJ5QiLD7gVlC6IS8g45fN7z.jpg' } },
      { plays: 1, rating: 10, watched_at: '2024-01-14T22:30:00.000Z', episode: { season: 2, number: 5, title: 'New Episode', ids: { trakt: 102, tvdb: 3, imdb: 'tt102', tmdb: 102 } }, show: { title: 'Succession', year: 2018, ids: { trakt: 3, slug: 'succession', tvdb: 328689, imdb: 'tt7660850', tmdb: 76331 }, poster: 'https://image.tmdb.org/t/p/w342/7QSM3Nd8d5y85XZbH9M9X3LwJyY.jpg' } },
    ] as WatchedEpisode[],
  },
});
